import { Box, Checkbox, Stack } from "@mui/material";
import type React from "react";
import { useCallback, useContext, useMemo } from "react";
import type { IListVirtualizedSelectContext } from "./types";

interface ISelectableRowProps<T> {
  index: number;
  data: T;
  context: React.Context<IListVirtualizedSelectContext<T>>;
}

export const selectableItemContent = <T,>(
  index: number,
  data: T,
  context: React.Context<IListVirtualizedSelectContext<T>>
) => <SelectableRow index={index} data={data} context={context} />;

export const SelectableRow = <T,>({ index, data, context }: ISelectableRowProps<T>) => {
  const { itemContent, getId, selectedItems, handleSelect, ListItemStack, ListItemCheckbox, ListItemBox } =
    useContext(context);

  const isSelected = useMemo(
    () =>
      !!selectedItems.find(
        (selectedItem, selectedItemIndex) => getId(selectedItem, selectedItemIndex) === getId(data, index)
      ),
    [selectedItems, data, index, getId]
  );

  const handleChange = useCallback(() => {
    handleSelect(data, index);
  }, [data, index, handleSelect]);

  return (
    <Stack
      direction="row"
      spacing={2}
      {...ListItemStack}
      sx={[
        {
          alignItems: "center",
        },
        ...(Array.isArray(ListItemStack?.sx) ? ListItemStack.sx : [ListItemStack?.sx]),
      ]}
    >
      <Checkbox checked={isSelected} onChange={handleChange} {...ListItemCheckbox} />
      <Box
        {...ListItemBox}
        sx={[
          {
            flex: "auto",
            overflow: "hidden",
          },
          ...(Array.isArray(ListItemBox?.sx) ? ListItemBox.sx : [ListItemBox?.sx]),
        ]}
      >
        {itemContent(index, data, context)}
      </Box>
    </Stack>
  );
};
